import { Component, OnInit } from '@angular/core';
import { InvoiceInterface, PaymentInterface } from '@contracts/interfaces/invoice.interface';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { ACCOUNT } from 'src/app/accounts';
import { InvoicesService } from 'src/app/services/invoices.service';
import { TransactionsService } from 'src/app/services/transactions.service';
import { firstValueFrom } from 'rxjs';

interface PaymentRow {
  invoiceNumber: number;
  invoiceType?: string;
  invoiceStatus?: string;
  paymentId?: string;
  paymentIndex?: number;
  transactionSha1?: string | null;
  customer?: any;
  payment: PaymentInterface;
  total?: number;
  amountDue?: number;
  dateCreated?: Date | string;
}

interface LinkCandidate {
  payment: PaymentRow;
  transaction: Transaction;
  reason: string;
  status: 'ready' | 'linked' | 'failed' | 'skipped';
  error?: string;
}

interface SkippedPayment {
  payment: PaymentRow;
  reason: string;
  matches: Transaction[];
  status?: 'ready' | 'linked' | 'failed';
  error?: string;
}

@Component({
  selector: 'app-payment-linker-once',
  templateUrl: './payment-linker-once.component.html',
  styleUrls: ['./payment-linker-once.component.scss']
})
export class PaymentLinkerOnceComponent implements OnInit {
  isLoading = false;
  isLinking = false;
  payments: PaymentRow[] = [];
  transactions: Transaction[] = [];
  candidates: LinkCandidate[] = [];
  skippedPayments: SkippedPayment[] = [];
  skippedCount = 0;
  linkedCount = 0;
  failedCount = 0;
  selectedTransactionSha1: string | null = null;

  constructor(
    private invoicesService: InvoicesService,
    private transactionsService: TransactionsService
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  async loadPreview() {
    this.isLoading = true;
    this.linkedCount = 0;
    this.failedCount = 0;
    this.candidates = [];
    this.skippedPayments = [];

    try {
      const [payments, transactions] = await Promise.all([
        firstValueFrom(this.invoicesService.getPayments({}, null)) as Promise<PaymentRow[]>,
        firstValueFrom(this.transactionsService.find({ 'source.account': ACCOUNT.REVENUE }, 0, 0, { date: -1 })) as Promise<Transaction[]>
      ]);

      this.payments = payments || [];
      this.transactions = transactions || [];
      const preview = this.buildPreview(this.payments, this.transactions);
      this.candidates = preview.candidates;
      this.skippedPayments = preview.skippedPayments;
      this.skippedCount = this.skippedPayments.length;
    } finally {
      this.isLoading = false;
    }
  }

  async linkAllCertainMatches() {
    if (this.isLinking || !this.candidates.length) {
      return;
    }

    this.isLinking = true;
    this.linkedCount = 0;
    this.failedCount = 0;

    for (const candidate of this.candidates) {
      if (candidate.status !== 'ready') {
        continue;
      }

      try {
        await firstValueFrom(this.invoicesService.linkPaymentToTransaction(
          candidate.payment.invoiceNumber,
          candidate.payment.payment?.paymentId || candidate.payment.paymentId,
          candidate.transaction.source.sha1,
          candidate.payment.paymentIndex
        ));
        candidate.status = 'linked';
        this.linkedCount++;
      } catch (error: any) {
        candidate.status = 'failed';
        candidate.error = error?.error?.error || 'Link failed';
        this.failedCount++;
      }
    }

    this.isLinking = false;
  }

  getCustomer(payment: PaymentRow): string {
    const customer = payment?.customer;
    if (!customer) {
      return '';
    }
    return customer.name || customer.business || '';
  }

  get oneMatchSkippedPayments(): SkippedPayment[] {
    return this.skippedPayments.filter(skipped => skipped.matches.length === 1);
  }

  openSkippedInvoice(skipped: SkippedPayment) {
    this.openInvoice({ payment: skipped.payment } as LinkCandidate);
  }

  openSkippedTransaction(transaction: Transaction) {
    if (!transaction?.source?.sha1) {
      return;
    }

    const index = this.transactions.findIndex(tx => tx?.source?.sha1 === transaction.source.sha1);
    this.transactionsService.openTransactionDialog(
      this.transactionsService.getTransactionType(transaction),
      transaction.source.sha1,
      index >= 0 ? index : 0,
      this.transactions,
      () => {}
    );
  }

  async linkSkippedPayment(skipped: SkippedPayment) {
    if (!skipped?.payment || skipped.matches.length !== 1 || skipped.status === 'linked') {
      return;
    }

    skipped.status = 'ready';
    skipped.error = null;

    try {
      await firstValueFrom(this.invoicesService.linkPaymentToTransaction(
        skipped.payment.invoiceNumber,
        skipped.payment.payment?.paymentId || skipped.payment.paymentId,
        skipped.matches[0].source.sha1,
        skipped.payment.paymentIndex
      ));
      skipped.status = 'linked';
    } catch (error: any) {
      skipped.status = 'failed';
      skipped.error = error?.error?.error || 'Link failed';
    }
  }

  openInvoice(candidate: LinkCandidate) {
    this.selectCandidate(candidate);
    const invoiceNumber = candidate?.payment?.invoiceNumber;
    if (!invoiceNumber) {
      return;
    }

    this.invoicesService.get(invoiceNumber).subscribe((invoice: InvoiceInterface) => {
      if (!invoice) {
        return;
      }

      this.invoicesService.openInvoiceDialog(invoice, () => {});
    });
  }

  openTransaction(candidate: LinkCandidate) {
    this.selectCandidate(candidate);
    const transaction = candidate?.transaction;
    if (!transaction?.source?.sha1) {
      return;
    }

    const index = this.transactions.findIndex(tx => tx?.source?.sha1 === transaction.source.sha1);
    this.transactionsService.openTransactionDialog(
      this.transactionsService.getTransactionType(transaction),
      transaction.source.sha1,
      index >= 0 ? index : 0,
      this.transactions,
      () => {}
    );
  }

  isSelected(candidate: LinkCandidate): boolean {
    return !!candidate?.transaction?.source?.sha1 &&
      candidate.transaction.source.sha1 === this.selectedTransactionSha1;
  }

  private buildPreview(payments: PaymentRow[], transactions: Transaction[]): { candidates: LinkCandidate[]; skippedPayments: SkippedPayment[] } {
    const unlinkedPayments = (payments || []).filter(payment => !payment.transactionSha1 && payment?.payment?.amount != null);
    const linkedTransactionHashes = new Set(
      (payments || [])
        .map(payment => payment.transactionSha1)
        .filter((sha1): sha1 is string => !!sha1)
    );

    const possibleMatches = new Map<PaymentRow, Transaction[]>();
    const amountMatches = new Map<PaymentRow, Transaction[]>();

    for (const payment of unlinkedPayments) {
      const matchingAmountTransactions = (transactions || []).filter(transaction =>
        !linkedTransactionHashes.has(transaction?.source?.sha1) &&
        +transaction.amount === +payment.payment.amount
      );
      const matches = matchingAmountTransactions.filter(transaction =>
        this.isCertainPaymentTransactionMatch(payment, transaction)
      );
      amountMatches.set(payment, matchingAmountTransactions);
      possibleMatches.set(payment, matches);
    }

    const transactionUseCount = new Map<string, number>();
    for (const matches of possibleMatches.values()) {
      for (const transaction of matches) {
        const sha1 = transaction.source.sha1;
        transactionUseCount.set(sha1, (transactionUseCount.get(sha1) || 0) + 1);
      }
    }

    const candidates: LinkCandidate[] = [];
    const candidatePaymentKeys = new Set<string>();
    for (const [payment, matches] of possibleMatches.entries()) {
      if (matches.length !== 1) {
        continue;
      }

      const transaction = matches[0];
      if (transactionUseCount.get(transaction.source.sha1) !== 1) {
        continue;
      }

      candidates.push({
        payment,
        transaction,
        reason: this.getMatchReason(payment, transaction),
        status: 'ready'
      });
      candidatePaymentKeys.add(this.getPaymentKey(payment));
    }

    const skippedPayments = unlinkedPayments
      .filter(payment => !candidatePaymentKeys.has(this.getPaymentKey(payment)))
      .map(payment => {
        const matches = possibleMatches.get(payment) || [];
        const possibleTransactions = amountMatches.get(payment) || [];
        return {
          payment,
          matches: possibleTransactions,
          reason: this.getSkippedReason(payment, matches, possibleTransactions, transactionUseCount)
        };
      });

    return { candidates, skippedPayments };
  }

  private isCertainPaymentTransactionMatch(payment: PaymentRow, transaction: Transaction): boolean {
    if (!payment?.invoiceNumber || !transaction?.source?.sha1) {
      return false;
    }

    if (+transaction.amount !== +payment.payment.amount) {
      return false;
    }

    const linkedInvoiceNumber = +(transaction as any)?.invoice?.number;
    if (!isNaN(linkedInvoiceNumber)) {
      return linkedInvoiceNumber === +payment.invoiceNumber;
    }

    return this.descriptionHasInvoiceNumber(transaction.description, payment.invoiceNumber) &&
      this.isDateClose(payment.payment.date, transaction.date);
  }

  private selectCandidate(candidate: LinkCandidate) {
    this.selectedTransactionSha1 = candidate?.transaction?.source?.sha1 || null;
  }

  private getMatchReason(payment: PaymentRow, transaction: Transaction): string {
    const linkedInvoiceNumber = +(transaction as any)?.invoice?.number;
    if (!isNaN(linkedInvoiceNumber)) {
      return `Transaction is already linked to invoice #${payment.invoiceNumber} and amount matches.`;
    }

    return `Transaction description contains invoice #${payment.invoiceNumber}, amount matches, and dates are close.`;
  }

  private getSkippedReason(payment: PaymentRow, certainMatches: Transaction[], amountMatches: Transaction[], transactionUseCount: Map<string, number>): string {
    if (payment?.payment?.amount == null) {
      return 'Payment has no amount.';
    }

    if (amountMatches.length === 0) {
      return 'No same-amount revenue transaction found.';
    }

    if (certainMatches.length === 0) {
      return `${amountMatches.length} same-amount transaction${amountMatches.length === 1 ? '' : 's'} found, but invoice/date evidence was not certain.`;
    }

    if (certainMatches.length > 1) {
      return `${certainMatches.length} certain-looking revenue transactions matched.`;
    }

    const match = certainMatches[0];
    if ((transactionUseCount.get(match.source.sha1) || 0) > 1) {
      return 'Matched transaction is also a candidate for another payment.';
    }

    return 'Not certain enough to auto-link.';
  }

  private getPaymentKey(payment: PaymentRow): string {
    return [
      payment.invoiceNumber,
      payment.payment?.paymentId || payment.paymentId || payment.paymentIndex,
      payment.payment?.amount,
      payment.payment?.date
    ].join('|');
  }

  private descriptionHasInvoiceNumber(description: string, invoiceNumber: number): boolean {
    const escaped = String(invoiceNumber).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|\\D)${escaped}(\\D|$)`, 'i').test(description || '');
  }

  private isDateClose(paymentDate: Date | string, transactionDate: Date | string): boolean {
    if (!paymentDate || !transactionDate) {
      return false;
    }

    const paymentTime = new Date(paymentDate).getTime();
    const transactionTime = new Date(transactionDate).getTime();
    if (isNaN(paymentTime) || isNaN(transactionTime)) {
      return false;
    }

    const dayMs = 24 * 60 * 60 * 1000;
    return Math.abs(paymentTime - transactionTime) <= 14 * dayMs;
  }
}
